import { t } from 'i18next';
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { Loader } from '~/components';
import { generateNameFormatted } from '~/helpers/titleHelper';
import { AnalysisLayout } from '~/pages/Analysis/AnalysisLayout';
import { Headers2 } from '~/pages/Analysis/Header';
import {
  BottomLayer, ButtonExport, Container, ContainerLoader,
} from './style';
import { apiCallDownload } from '~/providers';
import { toast } from 'react-toastify';

interface PowerBIEmbedProps {
  height?: string;
  width?: string;
}

type TabBase = {
  namePage: string;
  url: string;
};

const TABS_BASE: TabBase[] = [
  {
    namePage: 'dashboard',
    url: 'https://app.powerbi.com/view?r=eyJrIjoiZjcwMjljNDQtMjA4MS00ZTc2LTgwZmQtN2RlZDk2N2IwMWI0IiwidCI6IjNlNmM3M2UzLTkxYzctNGQ2Yy1iZjc1LWNjZDUwOThmM2JhMyJ9',
  },
];

export const PredictiveBB: React.FC<PowerBIEmbedProps> = () => {
  const [isReady, setIsReady] = useState(false);
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hasError, setHasError] = useState(false);

  const history = useHistory();
  const queryPars = queryString.parse(history.location.search);
  const linkBase = history.location.pathname;

  const titles = useMemo(
    () => ({
      dashboard: t('saudeAtual'),
      permanence: t('permanenciaStatus'),
      historic: t('historico'),
    }),
    [t],
  );

  const tabsRefs = useMemo(() => TABS_BASE.map(() => React.createRef<HTMLAnchorElement>()), []);

  const currentType = (typeof queryPars.type === 'string' && queryPars.type) || TABS_BASE[0].namePage;

  const allTabs = useMemo(() => TABS_BASE.map((tab, idx) => {
    const link = `${linkBase}?${queryString.stringify({
      ...queryPars,
      type: tab.namePage,
    })}`;
    const title = titles[tab.namePage as keyof typeof titles] ?? tab.namePage;
    return {
      title,
      label: title,
      link,
      isActive: currentType === tab.namePage,
      visible: true,
      ref: tabsRefs[idx],
      url: tab.url,
    };
  }),
  [currentType, linkBase, queryPars, tabsRefs, titles]);
  const handleIframeError = () => {
    setHasError(true);
    setIsReady(false);
  };

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const failTimer = setTimeout(() => {
      setHasError(true);
    }, 5000);
    timeoutRef.current = failTimer;
    setUrl(activeTab.url);
    setIsReady(false);
  }, [currentType]);

  const handleIframeLoad = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsReady(true), 5000);
  };

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const activeTab = allTabs.find((t) => t.isActive)!;

  async function exportTable() {
    try {
      setIsLoading(true);
      toast.info(t('donwloadIniado'));
      const exportResponse = await apiCallDownload('/predictive/export', {
      });

      const link: any = document.getElementById('downloadLink');
      if (link.href !== '#') window.URL.revokeObjectURL(link.href);
      link.href = window.URL.createObjectURL(exportResponse.data);
      link.download = exportResponse.headers.filename || `${t('nomeArquivoPreditivoBB')}.xlsx`;
      link.click();

      toast.success(t('sucessoExportacao'));
    } catch (error) {
      console.log(error);
      toast.error(t('naoFoiPossivelExportar'));
    }
    setIsLoading(false);
  }

  return (
    <>
      <Helmet>
        <title>{generateNameFormatted('Celsius 360', t('preditivo'))}</title>
      </Helmet>
      <AnalysisLayout />
      <div style={{ paddingTop: '10px' }}>
        <Headers2 links={allTabs} />
      </div>
      <a href="#" style={{ display: 'none' }} id="downloadLink" />
      <Container>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonExport onClick={() => exportTable()} disabled={isLoading}>
            {t('exportarTabela')}
          </ButtonExport>
        </div>
        <ReturnIframe url={url} iframeRef={iframeRef} handleIframeLoad={handleIframeLoad} isReady={isReady} handleIframeError={handleIframeError} hasError={hasError} />
        {isReady && (
          <BottomLayer />
        )}

        {!isReady && (
          <ContainerLoader>
            <Loader />
            <p>{t('carregandoDados')}</p>
          </ContainerLoader>
        )}
        {hasError && (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <p>{t('erroRelatorio')}</p>
          </div>
        )}
      </Container>
    </>
  );
};

function ReturnIframe({
  url, iframeRef, isReady, handleIframeLoad, handleIframeError, hasError,
}) {
  return (
    <iframe
      key={url}
      ref={iframeRef}
      title="Dashboard Diel"
      src={url}
      width="100%"
      height="100%"
      style={{
        border: 'none',
        transform: 'scale(1.01)',
        transformOrigin: 'top center',
        display: hasError ? 'none' : isReady ? 'block' : 'none',
      }}
      onLoad={handleIframeLoad}
      onError={handleIframeError}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}
